import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges, OnInit, SimpleChange, Injectable } from '@angular/core';
import { NgbDate, NgbDatepickerI18n, NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';
import { UtilService } from 'src/app/shared/services/util.service';
import * as moment from 'moment';

export const I18N_VALUES = {
    en: {
        weekdays: ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'],
        months: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
    },
    es: {
        weekdays: ['Lu', 'Ma', 'Mi', 'Ju', 'Vi', 'Sa', 'Do'],
        months: ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'],
    }
};

// Define a service holding the language. You probably already have one if your app is i18ned.
@Injectable()
export class I18n {
language = 'es';
}

// Define custom service providing the months and weekdays translations
@Injectable()
export class CustomDatepickerComponent extends NgbDatepickerI18n {

    constructor(private _i18n: I18n) {
        super();
    }

    getWeekdayShortName(weekday: number): string {
        return I18N_VALUES[this._i18n.language].weekdays[weekday - 1];
    }
    getMonthShortName(month: number): string {
        return I18N_VALUES[this._i18n.language].months[month - 1];
    }
    getMonthFullName(month: number): string {
        return this.getMonthShortName(month);
    }

    getDayAriaLabel(date: NgbDateStruct): string {
        return date.day + '/'
            + date.month + '/'
            + date.year;
    }
}

@Component({
    selector: 'app-datepicker',
    templateUrl: './datepicker.component.html',
    styleUrls: ['../fd-datepicker.component.scss'],
    providers: [I18n, {provide: NgbDatepickerI18n, useClass: CustomDatepickerComponent}]
})
/**
 * Componente que extiende la funcionalidad de ngb-datepicker
 * recibe como parametros
 * @param minDate fecha minima de selección
 * @param maxDate fecha maxima de selección
 * @param placeholder texto que se muestra en el campo cuando no se provee información
 * @param disabled indica si el campo y el calendario se encuentran habilitados
 * @param fromDateChange salida que retorna el valor actualizado de la fecha
 * @param fromDate recibe el valor con el que debe iniciar el calendario de ser necesario
 *                  se puede usar tanto de entrada como salida para la actualizacion de la fecha
 *                  asi: [(fromDate)]="valor" y no es necesario capturar el evento de fromDateChange
 * @author jnmartinez
 */
export class DatepickerComponent implements OnInit, OnChanges {
    @Input() minDate: Date;
    @Input() maxDate: Date;
    @Input() fromDate: Date;
    @Output() fromDateChange = new EventEmitter();
    @Input() placeholder: string;
    @Input() disabled: boolean;
    @Input() readonly: boolean;

    from: NgbDate;
    min: NgbDate;
    max: NgbDate;
    public model;
    public arrows = 'select';
    public open = false;

    constructor(private utilService: UtilService, private _i18n: I18n) { }

    set language(language: string) {
        this._i18n.language = language;
    }

    get language() {
        return this._i18n.language;
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes) {
            if (this.validarCambio(changes.fromDate)) {
                this.from = this.utilService.toStruct(changes.fromDate.currentValue);
                this.convertirValor();
            } else {
                this.reiniciarCampos();
            }
            if (this.validarCambio(changes.minDate)) {
                this.min = this.utilService.toStruct(changes.minDate.currentValue);
            }
            if (this.validarCambio(changes.maxDate)) {
                this.max = this.utilService.toStruct(changes.maxDate.currentValue);
            }

        }
    }

    /**
     * Método que valida si se presentó algún cambio en el campo recibido usando ngOnChanges
     * @param atributo variable a evaluar
     * @author jnmartinez
     */
    validarCambio(atributo: SimpleChange) {
        return atributo && atributo.currentValue
            && atributo.currentValue.valueOf() !== (atributo.previousValue ? atributo.previousValue.valueOf() : undefined);
    }

    ngOnInit() {
        if (this.fromDate) {
            this.from = this.utilService.toStruct(this.fromDate);
            this.convertirValor();
        }
        if (this.maxDate) {
            this.max = this.utilService.toStruct(this.maxDate);
        }
        if (this.minDate) {
            this.min = this.utilService.toStruct(this.minDate);
        }
    }

    /**
     * Método que obtiene el texto escrito en el campo y evalua si es
     * una fecha valida o no. En caso de no se valida se borra el texto escrito
     * y se borra el valor de la fecha si existia una seleccionada
     * @author jnmartinez
     */
    actualizarFecha() {
        const fechaIngresada = moment(this.model, 'D/M/YYYY', true);
        if (fechaIngresada.isValid()) {
            this.validarRangos(fechaIngresada);
        } else {
            this.reiniciarCampos();
        }
    }

    /**
     * Método que determina si en caso de existir rango minimo o maximo o ambos
     * si la fecha ingresada cumple con la condición
     * @param fechaIngresada objeto de tipo moment.Moment utilizado para evaluar la fecha ingresada
     * @author jnmartinez
     */
    validarRangos(fechaIngresada: moment.Moment) {
        if (this.maxDate && this.minDate && !fechaIngresada.isBetween(this.minDate, this.maxDate)) {
            this.reiniciarCampos();
        } else if (this.maxDate || this.minDate) {
            if (this.minDate && fechaIngresada.isBefore(this.minDate)) {
                this.reiniciarCampos();
            } else if (this.maxDate && fechaIngresada.isAfter(this.maxDate)) {
                this.reiniciarCampos();
            } else {
                this.implementarCambio(fechaIngresada.toDate());
            }
        } else {
            this.implementarCambio(fechaIngresada.toDate());
        }
    }

    /**
     * Método que actualiza los modelos utilizados para la fecha
     * y notifica el cambio
     * @param fechaIngresada nuevo valor de la fecha
     * @author jnmartinez
     */
    implementarCambio(fechaIngresada: Date) {
        this.from = this.utilService.toStruct(fechaIngresada);
        this.fromDateChange.emit(fechaIngresada);
    }

    /**
     * Método que borra la información tanto del campo de texto como del calendario
     * y notifica el cambio
     * @author jnmartinez
     */
    reiniciarCampos() {
        this.model = undefined;
        this.from = undefined;
        this.fromDateChange.emit(undefined);
    }

    /**
     * Método que recibe la fecha seleccionada en calendario y la transforma en Date
     * para despues notificar el cambio
     * @param date fecha recibida del calendario
     * @author jnmartinez
     */
    onDateSelection(date: NgbDate) {
        this.from = date;
        this.fromDateChange.emit(this.utilService.toDate(this.from));
        this.convertirValor();
        this.openCalendar();
    }

    /**
     * Método que convierte en texto el objeto de fecha seleccionado
     * @author jnmartinez
     */
    convertirValor() {
        this.model =
            this.from.day + '/'
            + this.from.month + '/'
            + this.from.year;
    }

    /**
     * Método que abre o cierra el calendario dependiendo del estado en que se encuentra
     * @author jnmartinez
     */
    openCalendar() {
        this.open = !this.open;
    }
}
